'use client'

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import SuggestionDropdown, {
    handleSuggestionKeyDown,
    type SuggestionItem
} from '@tebuto/forms/address-autocomplete/SuggestionDropdown'
import { GERMAN_ZIP_REGEX, useCitySuggestions } from '@tebuto/forms/address-autocomplete/use-address-lookup'
import { cn } from '@tebuto/lib/utils'
import { useField, useFormikContext } from 'formik'
import { type ChangeEvent, type JSX, useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import Description from './Description'
import ErrorMessage from './ErrorMessage'
import FormField, { type IFormField } from './Field'
import { Label } from './Label'
import { useFieldErrorVisible } from './useFieldErrorVisible'

type CityFormFieldProps = Omit<IFormField, 'formatter' | 'type'> & {
    /** Formik field name of the sibling zip field — selecting a suggestion autofills it */
    zipFieldName?: string
    /** Public API base URL override for the address lookup (used by the embedded widget) */
    lookupBaseUrl?: string
}

/**
 * City input with name typeahead. Suggestions only appear while the sibling zip
 * is empty or incomplete (with a valid zip the city gets autofilled by the zip
 * field instead); selecting a suggestion fills city and zip.
 */
export default function CityFormField({
    className = '',
    fieldClassName,
    labelClassName,
    label,
    name,
    autoComplete = 'address-level2',
    labelTooltip,
    inputTooltip,
    description,
    placeholder,
    mandatory,
    readOnly,
    showErrors = true,
    height = 'default',
    zipFieldName,
    lookupBaseUrl,
    'data-testid': testId
}: CityFormFieldProps): JSX.Element {
    const showFieldError = useFieldErrorVisible(name)
    const [field, , helpers] = useField<string>(name)
    const { values, setFieldValue } = useFormikContext<unknown>()
    const isWidgetField = fieldClassName?.includes('widget-text-primary') ?? false
    const inputTooltipId = inputTooltip ? `${name}-input` : undefined

    const siblingZip = zipFieldName ? getNestedValue(values, zipFieldName) : undefined
    const zipIsComplete = typeof siblingZip === 'string' && GERMAN_ZIP_REGEX.test(siblingZip)

    // Only queries the user actually typed trigger suggestions — never initial form values
    const [typedQuery, setTypedQuery] = useState('')
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const { suggestions } = useCitySuggestions(typedQuery, { enabled: !zipIsComplete, baseUrl: lookupBaseUrl })

    const items: SuggestionItem[] = useMemo(
        () =>
            suggestions.map((locality) => ({
                id: `${locality.postalCode}-${locality.name}`,
                label: locality.name,
                detail: locality.postalCode
            })),
        [suggestions]
    )

    useEffect(() => {
        if (!typedQuery) {
            return
        }
        setActiveIndex(0)
        setDropdownOpen(suggestions.length > 0)
    }, [suggestions, typedQuery])

    if (readOnly) {
        return (
            <FormField
                className={className}
                fieldClassName={fieldClassName}
                labelClassName={labelClassName}
                label={label}
                name={name}
                autoComplete={autoComplete}
                labelTooltip={labelTooltip}
                inputTooltip={inputTooltip}
                description={description}
                placeholder={placeholder}
                mandatory={mandatory}
                readOnly={readOnly}
                showErrors={showErrors}
                height={height}
                data-testid={testId}
            />
        )
    }

    function getHeight(): string {
        switch (height) {
            case 'full':
                return 'h-full'
            case 'small':
                return 'h-8'
            default:
                return 'h-10'
        }
    }

    function inputClassName(): string {
        let classes =
            'h-full rounded-lg flex-1 min-w-0 block w-full px-2 py-1 border text-sm border-secondary text-foreground-tertiary shadow-xs focus:outline-none focus:ring-0 focus:border-primary-500'
        if (!isWidgetField) {
            classes += ' placeholder:text-foreground-muted'
        }
        if (showFieldError) {
            classes += ' border-red-500'
        }
        return classes
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        void helpers.setValue(event.target.value)
        setTypedQuery(event.target.value)
        if (!event.target.value.trim()) {
            setDropdownOpen(false)
        }
    }

    function selectCity(item: SuggestionItem): void {
        void helpers.setValue(item.label)
        if (zipFieldName && item.detail) {
            void setFieldValue(zipFieldName, item.detail)
        }
        setTypedQuery('')
        setDropdownOpen(false)
    }

    return (
        <div className={cn('relative', className)}>
            {label ? (
                <div className="flex w-full items-center justify-between gap-2">
                    <Label htmlFor={name} mandatory={mandatory} className={labelClassName}>
                        {label}
                    </Label>
                    {labelTooltip ? (
                        <QuestionMarkCircleIcon
                            className="h-5 w-5 shrink-0 text-foreground-muted inline"
                            data-tooltip-id={name}
                            data-tooltip-content={labelTooltip}
                        />
                    ) : null}
                </div>
            ) : null}
            <div
                className={cn('flex rounded-md shadow-xs relative', getHeight())}
                {...(inputTooltipId
                    ? {
                          'data-tooltip-id': inputTooltipId,
                          'data-tooltip-content': inputTooltip
                      }
                    : {})}
            >
                <input
                    data-testid={testId ?? `field-${name}`}
                    id={name}
                    name={name}
                    type="text"
                    autoComplete={autoComplete}
                    className={cn(inputClassName(), fieldClassName)}
                    required={mandatory}
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    onChange={handleChange}
                    onKeyDown={(event) =>
                        handleSuggestionKeyDown(event, {
                            open: dropdownOpen,
                            items,
                            activeIndex,
                            setActiveIndex,
                            onSelect: selectCity,
                            onClose: () => setDropdownOpen(false)
                        })
                    }
                    onBlur={(event) => {
                        setDropdownOpen(false)
                        field.onBlur(event)
                    }}
                    role="combobox"
                    aria-expanded={dropdownOpen}
                    aria-controls={`${name}-suggestions`}
                />
                {dropdownOpen ? (
                    <SuggestionDropdown
                        id={`${name}-suggestions`}
                        items={items}
                        activeIndex={activeIndex}
                        onSelect={selectCity}
                        onActiveIndexChange={setActiveIndex}
                        isWidget={isWidgetField}
                    />
                ) : null}
            </div>
            {labelTooltip ? <Tooltip id={name} /> : null}
            {inputTooltipId ? (
                <Tooltip
                    id={inputTooltipId}
                    positionStrategy="fixed"
                    opacity={1}
                    className="z-9999! max-w-xs! rounded! bg-primary-800! p-3! text-xs! font-medium! leading-snug! text-white!"
                />
            ) : null}
            {showErrors ? <ErrorMessage name={name} /> : null}
            <Description description={description} />
        </div>
    )
}

export function getNestedValue(values: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key) => {
        if (current && typeof current === 'object') {
            return (current as Record<string, unknown>)[key]
        }
        return undefined
    }, values)
}
